import { useCallback, useReducer, useMemo, Dispatch, useRef, Key, Reducer } from 'react';
import { debounce } from '@agilepoint/utils-common';

type Record = {
    id: Key;
    [key: string]: any;
};

interface IUpsertAction<TRecord> {
    type: 'UPSERT';
    payload: TRecord[];
}

interface IRemoveAction {
    type: 'REMOVE';
    payload: Key[];
}

interface IClearAction {
    type: 'CLEAR';
    payload?: never;
}

type MicroStoreAction<TRecord> = IUpsertAction<TRecord> | IRemoveAction | IClearAction;

interface IMicroStore<TRecord> {
    [id: string]: TRecord;
}

function addItemToState<TRecord extends Record>(state: IMicroStore<TRecord>, item: TRecord) {
    state[item.id] = item;
    return state;
}

function removeItemFromState<TRecord extends Record>(state: IMicroStore<TRecord>, id: Key) {
    delete state[id];
    return state;
}

function reducer<TRecord extends Record>(state: IMicroStore<TRecord>, action: MicroStoreAction<TRecord>) {
    switch (action.type) {
        case 'UPSERT':
            return action.payload.reduce(addItemToState, { ...state });

        case 'REMOVE':
            return action.payload.reduce(removeItemFromState, { ...state });

        case 'CLEAR':
            return {};

        default:
            return state;
    }
}

type PayloadOf<TSR, TAction extends MicroStoreAction<TSR>, TActionType extends TAction['type']> =
    TAction extends { type: TActionType } ? TAction['payload'] extends Array<infer P> ? P : never : never;

type UseDispatchAggregatorReturnType<TPayload> = [(payload: TPayload) => void, () => void];

function useDispatchAggregator<TSR extends Record, TMSA extends MicroStoreAction<TSR>, TActionType extends TMSA['type'], TPayload extends PayloadOf<TSR, TMSA, TActionType>>
    (dispatch: Dispatch<TMSA>, type: TActionType, disable?: boolean): UseDispatchAggregatorReturnType<TPayload> {
    const payloadList = useRef([] as TPayload[]);
    const isDetached = useRef(false);

    const dispatcher = useCallback((args: any) => {
        if (isDetached.current) {
            return;
        }
        dispatch(args);
    }, [dispatch]);

    const bulkDispatcher = useMemo(() => debounce((args: any) => {
        dispatcher(args);
        payloadList.current = [];
    }, 200, null), [dispatcher]);

    const dispatchAggregator = useCallback((payload: TPayload) => {
        if (disable) {
            dispatcher({
                type,
                payload: [payload]
            });
            return;
        }
        payloadList.current.push(payload);
        bulkDispatcher({
            type,
            payload: payloadList.current
        });
    }, [bulkDispatcher, disable, dispatcher, type]);

    const detach = useCallback(() => {
        isDetached.current = true;
    }, []);

    return useMemo(() => ([dispatchAggregator, detach]), [dispatchAggregator, detach]);
}

interface IMicroStoreOptions {
    disableDispatchAggregator?: boolean
}

export function useMicroStore<TSR extends Record>(options: IMicroStoreOptions = {}) {
    const { disableDispatchAggregator } = options;
    const [store, dispatch] = useReducer<Reducer<IMicroStore<TSR>, MicroStoreAction<TSR>>>(reducer, {});
    const [upsert, detachUpsert] = useDispatchAggregator(dispatch, 'UPSERT', disableDispatchAggregator);
    const [remove, detachRemove] = useDispatchAggregator(dispatch, 'REMOVE', disableDispatchAggregator);
    const clear = useCallback(() => dispatch({ type: 'CLEAR' }), [dispatch]);
    const detachStore = useCallback(() => {
        detachUpsert();
        detachRemove();
    }, [detachUpsert, detachRemove]);
    const items = useMemo(() => Object.values(store), [store]);
    return { items, upsert, remove, clear, detachStore };
}
